import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  createBulletin,
  deleteBulletin,
  deleteBulletinAttachment,
  downloadBulletinAttachment,
  fetchBulletin,
  fetchBulletins,
  updateBulletin,
  uploadBulletinAttachments,
} from '../../api/bulletin';
import { useCompanyStore } from '../../store/companyStore';
import { BulletinAttachment, BulletinPost } from '../../types/bulletin';
import {notifyError, reportError} from '../../utils/notify';
import ListPager from '../common/ListPager';

import BulletinComments from './BulletinComments';
import './BulletinPanel.css';

type View =
  | { type: 'list' }
  | { type: 'detail'; postId: number }
  | { type: 'create' }
  | { type: 'edit'; postId: number };

const LIST_VIEW = { type: 'list' } as const;

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  return value.replace('T', ' ').slice(0, 16);
}

function formatSize(size?: number | null) {
  if (size == null) return '';
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
  return `${(size / (1024 * 1024)).toFixed(1)}MB`;
}

function BulletinPanel() {
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canPin = role === 'OWNER' || role === 'TEAM_LEADER';

  const [view, setView] = useState<View>(LIST_VIEW);
  const [rows, setRows] = useState<BulletinPost[]>([]);
  const [detail, setDetail] = useState<BulletinPost | null>(null);
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [pinned, setPinned] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<BulletinAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchBulletins({ keyword, page, size: pageSize });
      setRows(result.content ?? []);
      setTotalElements(result.totalElements ?? 0);
      setTotalPages(result.totalPages ?? 0);
      setError('');
    } catch {
      reportError(setError, '게시글을 불러오지 못했습니다.');
      setRows([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [keyword, page, pageSize]);

  useEffect(() => {
    setView(LIST_VIEW);
    setPage(0);
  }, [companyId, setView]);

  useEffect(() => {
    setPage(0);
  }, [keyword]);

  useEffect(() => {
    const timer = window.setTimeout(() => void loadList(), 200);
    return () => window.clearTimeout(timer);
  }, [loadList]);

  const openDetail = async (postId: number) => {
    try {
      setLoading(true);
      setError('');
      setDetail(await fetchBulletin(postId));
      setView({ type: 'detail', postId });
    } catch {
      notifyError('게시글을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const resetFormFiles = () => {
    setPendingFiles([]);
    setExistingFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openCreate = () => {
    setTitle('');
    setContent('');
    setPinned(false);
    setDetail(null);
    resetFormFiles();
    setView({ type: 'create' });
  };

  const openEdit = (post: BulletinPost) => {
    setTitle(post.title || '');
    setContent(post.content || '');
    setPinned(Boolean(post.pinned));
    setDetail(post);
    setPendingFiles([]);
    setExistingFiles(post.attachments ?? []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setView({ type: 'edit', postId: post.postId });
  };

  const backToList = () => {
    setView(LIST_VIEW);
    setDetail(null);
    resetFormFiles();
    void loadList();
  };

  const handleRemoveExisting = async (attachment: BulletinAttachment) => {
    if (view.type !== 'edit') return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    try {
      await deleteBulletinAttachment(view.postId, attachment.attachmentId);
      setExistingFiles((prev) => prev.filter((item) => item.attachmentId !== attachment.attachmentId));
    } catch {
      notifyError('첨부파일 삭제에 실패했습니다.');
    }
  };

  const detailAttachmentsMutable = view.type === 'detail' && Boolean(detail?.canEdit);

  const handleDetailUpload = async (files: FileList | null) => {
    if (!files?.length || !detail || !detailAttachmentsMutable) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
    try {
      setSaving(true);
      await uploadBulletinAttachments(detail.postId, Array.from(files));
      setDetail(await fetchBulletin(detail.postId));
      void loadList();
    } catch {
      notifyError('첨부파일 업로드에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDetailAttachmentDelete = async (attachment: BulletinAttachment) => {
    if (!detail || !detailAttachmentsMutable) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    try {
      setSaving(true);
      await deleteBulletinAttachment(detail.postId, attachment.attachmentId);
      setDetail(await fetchBulletin(detail.postId));
      void loadList();
    } catch {
      notifyError('첨부파일 삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      notifyError('제목을 입력해 주세요.');
      return;
    }
    if (!content.trim()) {
      notifyError('내용을 입력해 주세요.');
      return;
    }
    try {
      setSaving(true);
      let saved: BulletinPost;
      if (view.type === 'edit') {
        saved = await updateBulletin(view.postId, {
          title: title.trim(),
          content: content.trim(),
          pinned: canPin ? pinned : undefined,
        });
      } else {
        saved = await createBulletin({
          title: title.trim(),
          content: content.trim(),
          pinned: canPin ? pinned : false,
        });
      }
      if (pendingFiles.length > 0) {
        await uploadBulletinAttachments(saved.postId, pendingFiles);
        saved = await fetchBulletin(saved.postId);
      }
      setDetail(saved);
      resetFormFiles();
      setView({ type: 'detail', postId: saved.postId });
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '저장에 실패했습니다.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post: BulletinPost) => {
    if (!window.confirm(`"${post.title}" 글을 삭제할까요?`)) return;
    try {
      await deleteBulletin(post.postId);
      backToList();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '삭제에 실패했습니다.'
      );
    }
  };

  return (
    <main className="bulletin-panel">
      <header className="bulletin-panel__head">
        <div>
          <p className="bulletin-panel__eyebrow">Company Board</p>
          <h2>게시판</h2>
          <p className="bulletin-panel__guide">공지·공유 글에 파일과 댓글을 남길 수 있습니다.</p>
        </div>
        <div className="bulletin-panel__head-actions">
          {view.type === 'list' ? (
            <>
              <label className="bulletin-panel__search">
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="제목·내용·작성자 검색"
                />
              </label>
              <button type="button" className="bulletin-panel__btn is-ghost" onClick={() => void loadList()}>
                새로고침
              </button>
              <button type="button" className="bulletin-panel__btn is-primary" onClick={openCreate}>
                글쓰기
              </button>
            </>
          ) : (
            <button type="button" className="bulletin-panel__btn is-ghost" onClick={backToList}>
              목록으로
            </button>
          )}
        </div>
      </header>

      {error ? <p className="bulletin-panel__error">{error}</p> : null}

      {view.type === 'list' ? (
        <div className="bulletin-panel__body">
          {loading ? (
            <p className="bulletin-panel__hint">불러오는 중...</p>
          ) : rows.length === 0 ? (
            <div className="bulletin-panel__empty">
              <strong>등록된 글이 없습니다</strong>
              <p>첫 게시글을 작성해 보세요. 첨부파일과 댓글도 함께 쓸 수 있습니다.</p>
              <button type="button" className="bulletin-panel__btn is-primary" onClick={openCreate}>
                글쓰기
              </button>
            </div>
          ) : (
            <div className="paged-list">
              <div className="paged-list__scroll">
                <table className="bulletin-panel__table">
                  <thead>
                    <tr>
                      <th style={{ width: '72px' }}>No.</th>
                      <th style={{ width: '72px' }}>구분</th>
                      <th>제목</th>
                      <th style={{ width: '140px' }}>작성자</th>
                      <th style={{ width: '140px' }}>작성일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.postId} onClick={() => void openDetail(row.postId)}>
                        <td>{row.postNo ?? '-'}</td>
                        <td>
                          {row.pinned ? <span className="bulletin-panel__tag">공지</span> : '일반'}
                        </td>
                        <td className="is-title">{row.title}</td>
                        <td>{row.authorName || '-'}</td>
                        <td>{formatDateTime(row.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPager
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                loading={loading}
                onChange={setPage}
              />
            </div>
          )}
        </div>
      ) : null}

      {(view.type === 'create' || view.type === 'edit') && (
        <form className="bulletin-panel__form" onSubmit={(e) => void handleSubmit(e)}>
          <label className="bulletin-panel__field">
            <span>제목</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="제목을 입력하세요"
              required
            />
          </label>
          <label className="bulletin-panel__field">
            <span>내용</span>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={12}
              placeholder="내용을 입력하세요"
              required
            />
          </label>

          <div className="bulletin-panel__files">
            <div className="bulletin-panel__files-head">
              <strong>첨부파일</strong>
              <span>최대 10개</span>
            </div>
            {existingFiles.length > 0 ? (
              <ul className="bulletin-panel__file-list">
                {existingFiles.map((file) => (
                  <li key={file.attachmentId}>
                    <button
                      type="button"
                      className="bulletin-panel__file-link"
                      onClick={() =>
                        view.type === 'edit'
                          ? void downloadBulletinAttachment(view.postId, file.attachmentId, file.fileName)
                          : undefined
                      }
                    >
                      {file.fileName}
                      {file.fileSize != null ? ` (${formatSize(file.fileSize)})` : ''}
                    </button>
                    <button
                      type="button"
                      className="bulletin-panel__file-remove"
                      onClick={() => void handleRemoveExisting(file)}
                    >
                      삭제
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {pendingFiles.length > 0 ? (
              <ul className="bulletin-panel__file-list is-pending">
                {pendingFiles.map((file, index) => (
                  <li key={`${file.name}-${index}`}>
                    <span>
                      {file.name} ({formatSize(file.size)})
                    </span>
                    <button
                      type="button"
                      className="bulletin-panel__file-remove"
                      onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                    >
                      제거
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <label className="bulletin-panel__attach">
              파일 선택
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => {
                  const next = Array.from(e.target.files ?? []);
                  const room = Math.max(0, 10 - existingFiles.length - pendingFiles.length);
                  setPendingFiles((prev) => [...prev, ...next].slice(0, prev.length + room));
                  e.target.value = '';
                }}
              />
            </label>
          </div>

          {canPin ? (
            <label className="bulletin-panel__check">
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
              />
              공지로 등록 (목록 상단 고정)
            </label>
          ) : null}
          <div className="bulletin-panel__form-actions">
            <button type="button" className="bulletin-panel__btn is-ghost" onClick={backToList}>
              취소
            </button>
            <button type="submit" className="bulletin-panel__btn is-primary" disabled={saving}>
              {saving ? '저장 중...' : view.type === 'edit' ? '수정 저장' : '등록'}
            </button>
          </div>
        </form>
      )}

      {view.type === 'detail' && detail ? (
        <article className="bulletin-panel__detail">
          <div className="bulletin-panel__detail-head">
            <div>
              {detail.pinned ? <span className="bulletin-panel__tag">공지</span> : null}
              <h3>
                {detail.postNo != null ? `No.${detail.postNo} · ` : ''}
                {detail.title}
              </h3>
              <p>
                {detail.authorName || '-'} · {formatDateTime(detail.createdAt)}
                {detail.updatedAt && detail.updatedAt !== detail.createdAt
                  ? ` (수정 ${formatDateTime(detail.updatedAt)})`
                  : ''}
              </p>
            </div>
            <div className="bulletin-panel__detail-actions">
              {detail.canEdit ? (
                <button
                  type="button"
                  className="bulletin-panel__btn is-ghost"
                  onClick={() => openEdit(detail)}
                >
                  수정
                </button>
              ) : null}
              {detail.canDelete ? (
                <button
                  type="button"
                  className="bulletin-panel__btn is-danger"
                  onClick={() => void handleDelete(detail)}
                >
                  삭제
                </button>
              ) : null}
            </div>
          </div>
          <div className="bulletin-panel__content">{detail.content}</div>

          {(detail.attachments && detail.attachments.length > 0) || detailAttachmentsMutable ? (
            <div className="bulletin-panel__detail-files">
              <div className="bulletin-panel__files-head">
                <strong>첨부파일</strong>
                {detailAttachmentsMutable ? (
                  <label className="bulletin-panel__attach">
                    파일 추가
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      disabled={saving}
                      onChange={(e) => void handleDetailUpload(e.target.files)}
                    />
                  </label>
                ) : null}
              </div>
              {(detail.attachments ?? []).length > 0 ? <ul>
                {(detail.attachments ?? []).map((file) => (
                  <li key={file.attachmentId}>
                    <button
                      type="button"
                      onClick={() =>
                        void downloadBulletinAttachment(detail.postId, file.attachmentId, file.fileName)
                      }
                    >
                      {file.fileName}
                      {file.fileSize != null ? ` (${formatSize(file.fileSize)})` : ''}
                    </button>
                    {detailAttachmentsMutable ? (
                      <button
                        type="button"
                        className="bulletin-panel__btn is-danger"
                        disabled={saving}
                        onClick={() => void handleDetailAttachmentDelete(file)}
                      >
                        삭제
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul> : <p className="bulletin-panel__hint">등록된 첨부파일이 없습니다.</p>}
            </div>
          ) : null}

          <BulletinComments postId={detail.postId} />
        </article>
      ) : null}
    </main>
  );
}

export default BulletinPanel;
